import { useContext } from 'react';
import { useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';

import { ApplicationContext } from '../../../contexts/ApplicationContext';
import { contentsApi } from '../../../api/contents';
import { checkoutsApi } from '../../../api/checkouts';
import { PageBlocks, useCheckoutPage } from '../components/PageBlocks';
import { isPreviewMode } from '../components/PageBlocks/use-preview-bridge';
import { useCheckoutTracking } from '../components/PageBlocks/tracking';
import './styles.scss';

const { getBySlug } = contentsApi;
const { findByContent } = checkoutsApi;

/**
 * Página de confirmação de compra DO CHECKOUT (obrigado), montada no builder
 * (page.confirmation). O submit só redireciona para cá quando ela existe —
 * sem blocos configurados, o fluxo antigo (obrigado da plataforma) continua.
 *
 * Também atende o modo preview do builder (?_preview=1): o rascunho chega por
 * postMessage via useCheckoutPage, como no checkout.
 */
export const ThanksPage = () => {
	const { slug } = useParams();
	const [searchParams] = useSearchParams();
	const { customization } = useContext(ApplicationContext);

	const { data: content } = useQuery(
		[getBySlug.key, { slug: slug ?? '' }],
		getBySlug.fetch,
		{
			refetchOnWindowFocus: false
		}
	);
	const { data: checkout } = useQuery(
		[findByContent.key, { id: content?.id }],
		findByContent.fetch,
		{ refetchOnWindowFocus: false }
	);

	const page = useCheckoutPage(checkout?.id);
	// Pixels da etapa de obrigado: PageView + Purchase/conversion, com o
	// valor da compra vindo do redirect (?v=).
	useCheckoutTracking(
		page?.settings?.confirmationTracking,
		'confirmation',
		Number(searchParams.get('v')) || undefined
	);
	const receipt = searchParams.get('receipt');
	const hero = page?.settings?.confirmation ?? {};

	/**
	 * Link do botão com variáveis: {{receipt}}, {{valor}}, {{email}},
	 * {{nome}} e {{produto}} são substituídos na hora (URL-encoded), e as
	 * utm_* presentes na URL desta página são repassadas ao destino. E-mail e
	 * nome vêm do sessionStorage (gravados no redirect do checkout) — nunca
	 * pela URL.
	 */
	const resolveButtonUrl = (template?: string): string | undefined => {
		if (!template) return template;
		let buyer: { email?: string; name?: string } = {};
		try {
			buyer = JSON.parse(
				sessionStorage.getItem(
					`checkout-thanks-buyer:${window.location.pathname}`
				) || '{}'
			);
		} catch {
			buyer = {};
		}
		const vars: Record<string, string> = {
			receipt: receipt ?? '',
			valor: searchParams.get('v') ?? '',
			value: searchParams.get('v') ?? '',
			email: buyer.email ?? '',
			nome: buyer.name ?? '',
			name: buyer.name ?? '',
			produto: slug ?? '',
			product: slug ?? ''
		};
		let url = template.replace(
			/\{\{\s*(\w+)\s*\}\}/g,
			(match, key: string) => {
				const value = vars[key.toLowerCase()];
				return value !== undefined
					? encodeURIComponent(value)
					: match;
			}
		);
		// Repassa as UTMs da chegada ao destino do botão (sem sobrescrever).
		const utms: string[] = [];
		searchParams.forEach((value, key) => {
			if (
				key.toLowerCase().startsWith('utm_') &&
				!url.includes(`${key}=`)
			) {
				utms.push(`${key}=${encodeURIComponent(value)}`);
			}
		});
		if (utms.length > 0) {
			url += (url.includes('?') ? '&' : '?') + utms.join('&');
		}
		return url;
	};

	return (
		<div className="thanks-page">
			<div className="container">
				<div className="thanks-hero">
					<FaCheckCircle aria-hidden />
					<h1>{hero.title || 'Compra confirmada!'}</h1>
					<p>
						{hero.message ||
							'Enviamos o comprovante e as instruções de acesso para o seu e-mail.'}
					</p>
					{receipt && (
						<span className="thanks-receipt">
							Comprovante nº {receipt}
						</span>
					)}
					{(hero.buttonUrl || customization?.url) && (
						<a
							className="thanks-cta"
							href={
								resolveButtonUrl(hero.buttonUrl) ||
								customization?.url
							}
							style={{
								// Mesma técnica do botão de compra: inline
								// (a herança da var não é confiável aqui).
								backgroundColor:
									hero.buttonColor ||
									page?.settings?.accentColor ||
									undefined
							}}
						>
							{hero.buttonLabel || 'Acessar a plataforma'}
						</a>
					)}
				</div>

				{/* Blocos montados no builder (page.confirmation → blocks). */}
				<PageBlocks
					page={
						page
							? { ...page, blocks: page.confirmation ?? [] }
							: page
					}
					checkoutId={checkout?.id}
				/>
				{/* Área de solta no builder mesmo com a página vazia. */}
				{isPreviewMode() && !page?.confirmation?.length && (
					<div className="page-blocks" style={{ minHeight: 140 }} />
				)}
			</div>
		</div>
	);
};
